import { useState } from 'react';
import { useSuperMutation, useSuperQuery } from '@vadiun/react-hooks-legacy';
import { useSnackbar } from 'notistack';
import {
  contingencyService,
  ContingencyListParams,
  UpdateContingencyPayload
} from 'app/viaje/viajeTraslado/coordinar/services/contingencyService';

export interface ContingencyFilters {
  polygon_zone_id?: number;
  contingency_reason_id?: number;
  contingency_type_id?: number;
  applied_by_user_id?: number;
  applied_at?: string;
  finished_at?: string;
  observation?: string;
}

export interface ContingencyColumnVisibility {
  contingency_reason_id: boolean;
  contingency_type_id: boolean;
  applied_at: boolean;
  finished_at: boolean;
  duration_minutes: boolean;
  applied_by_user_name: boolean;
  polygon_zone_id: boolean;
  observation: boolean;
  updated_at: boolean;
}

export const contingencyColumnLabels: Record<
  keyof ContingencyColumnVisibility,
  string
> = {
  contingency_reason_id: 'Razón',
  contingency_type_id: 'Tipo',
  applied_at: 'Aplicada',
  finished_at: 'Finaliza',
  duration_minutes: 'Duración (min)',
  applied_by_user_name: 'Usuario',
  polygon_zone_id: 'Polígono',
  observation: 'Observación',
  updated_at: 'Actualizada'
};

const DEFAULT_FILTERS: ContingencyFilters = {};

const DEFAULT_COLUMN_VISIBILITY: ContingencyColumnVisibility = {
  contingency_reason_id: true,
  contingency_type_id: true,
  applied_at: true,
  finished_at: true,
  duration_minutes: true,
  applied_by_user_name: true,
  polygon_zone_id: true,
  observation: true,
  updated_at: true
};

const mutationErrorMessage = (error: unknown): string => {
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Ocurrió un error inesperado';
};

export const useContingencies = () => {
  const service = contingencyService();
  const { enqueueSnackbar } = useSnackbar();

  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sort, setSort] = useState<{ sortBy: string; sortDir: 'ASC' | 'DESC' }>(
    { sortBy: 'updated_at', sortDir: 'DESC' }
  );
  const [filters, setFilters] = useState<ContingencyFilters>(DEFAULT_FILTERS);
  const [columnVisibility, setColumnVisibility] =
    useState<ContingencyColumnVisibility>(DEFAULT_COLUMN_VISIBILITY);

  const buildParams = (): ContingencyListParams => ({
    page,
    page_size: rowsPerPage,
    sort: sort.sortBy,
    sort_dir: sort.sortDir,
    ...filters
  });

  const listQuery = useSuperQuery(
    () => service.getContingencies(buildParams()),
    { showSpinner: true },
    [page, rowsPerPage, sort.sortBy, sort.sortDir, JSON.stringify(filters)]
  );

  const onChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const onChangeRowsPerPage = (newRowsPerPage: number) => {
    setPage(1);
    setRowsPerPage(newRowsPerPage);
  };

  const onSortChange = (sortBy: string, direction: 'asc' | 'desc' | null) => {
    setSort({
      sortBy,
      sortDir: direction === 'asc' ? 'ASC' : 'DESC'
    });
    setPage(1);
  };

  const onFiltersChange = (next: ContingencyFilters) => {
    setFilters(next);
    setPage(1);
  };

  const resetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(1);
  };

  const updateMutation = useSuperMutation(
    (payload: { id: number; values: UpdateContingencyPayload }) =>
      service.updateContingency(payload.id, payload.values),
    {
      onMutationFinish() {
        listQuery.reload();
      },
      onError(error: unknown) {
        enqueueSnackbar(mutationErrorMessage(error), { variant: 'error' });
      },
      messages: {
        success: { message: 'La contingencia fue actualizada con éxito' }
      }
    } as any
  );

  const deleteMutation = useSuperMutation(
    (id: number) => service.deleteContingency(id),
    {
      onMutationFinish() {
        listQuery.reload();
      },
      onError(error: unknown) {
        enqueueSnackbar(mutationErrorMessage(error), { variant: 'error' });
      },
      messages: {
        success: { message: 'La contingencia fue eliminada con éxito' }
      }
    } as any
  );

  return {
    contingencies: {
      data: listQuery.data?.rows ?? [],
      total: Number(listQuery.data?.total ?? 0),
      isLoading: listQuery.isLoading,
      error: listQuery.error,
      reload: listQuery.reload
    },
    page,
    rowsPerPage,
    sort,
    filters,
    onChangePage,
    onChangeRowsPerPage,
    onSortChange,
    onFiltersChange,
    resetFilters,
    columnVisibility,
    setColumnVisibility,
    updateMutation,
    deleteMutation
  };
};

export const DEFAULT_CONTINGENCY_FILTERS = DEFAULT_FILTERS;
